import { InlineMessage, SettingRow, SettingsGroup, Spinner, Stack, Switch } from '@olytic/foundation';
import { useEffect, useState } from 'react';

import { authenticatedFetch } from '../auth/authClient';
import { humanize } from '../objects/format';
import { fetchTrackedFields, setTracked, type TrackedField, trackedSummary, trackingNote } from './fieldHistoryApi';

/** Setup → Objects → an object → History tracking: one switch per field, each saved as it flips. */
export function HistoryTrackingPanel({ object }: { object: string }): JSX.Element {
  const [fields, setFields] = useState<TrackedField[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setFields(null);
    setLoadError(null);
    fetchTrackedFields(authenticatedFetch, object, { signal: controller.signal }).then(
      (loaded) => setFields(loaded),
      (error: unknown) => {
        if (!controller.signal.aborted) setLoadError(error instanceof Error ? error.message : 'Could not load history tracking.');
      },
    );
    return () => controller.abort();
  }, [object]);

  async function toggle(field: TrackedField, tracked: boolean) {
    setSaving(field.name);
    setSaveError(null);
    try {
      const saved = await setTracked(authenticatedFetch, object, field.name, tracked);
      setFields((list) => list?.map((f) => (f.name === saved.name ? saved : f)) ?? list);
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Could not change tracking.');
    } finally {
      setSaving(null);
    }
  }

  if (loadError !== null) {
    return (
      <InlineMessage tone="error" role="alert">
        {loadError}
      </InlineMessage>
    );
  }
  if (fields === null) return <Spinner label="Loading history tracking…" />;

  return (
    <Stack>
      {saveError !== null ? (
        <InlineMessage tone="error" role="alert">
          {saveError}
        </InlineMessage>
      ) : null}
      <SettingsGroup
        title="History tracking"
        eyebrow={trackedSummary(fields)}
        description="Each change to a tracked field is kept with who made it and when. Every change is stored, so track only the fields you need."
      >
        {fields.map((field) => (
          <SettingRow
            key={field.name}
            label={humanize(field.name)}
            description={trackingNote(field)}
            control={(labelId) => (
              <Switch
                aria-labelledby={labelId}
                checked={field.tracked}
                disabled={saving !== null}
                onCheckedChange={(next) => void toggle(field, next)}
              />
            )}
          />
        ))}
      </SettingsGroup>
    </Stack>
  );
}
